import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import '../styles/FilterSidebar.css';
import { getSearchKeywords } from '../../../../services/accIqService';
import { industriesMap } from '../utils/industryConstants';
import NewsLetterDateRangePicker from './NewsLetterDateRangePicker';

const FilterSidebar = ({
  startDate = '',
  setStartDate,
  endDate = '',
  setEndDate,
  selectedNewsTypes = [],
  setSelectedNewsTypes,
  selectedRegions = [],
  setSelectedRegions,
  selectedCompanies = [],
  setSelectedCompanies,
  companyNamesMap = {},
  onCompanyNameMapReady,
  consumerIndustries = [],
  selectedIndustryNewsDomains = [],
  setSelectedIndustryNewsDomains,
  selectedCustomerDomains = [],
  setSelectedCustomerDomains,
  userIndustry = '',
  onlyWithTimeline,
  setOnlyWithTimeline
}) => {
  // Local state for date inputs (only update parent on Go click)
  const [localStartDate, setLocalStartDate] = useState(startDate || '');
  const [localEndDate, setLocalEndDate] = useState(endDate || '');

  // Sync with parent state changes
  useEffect(() => {
    setLocalStartDate(startDate || '');
    setLocalEndDate(endDate || '');
  }, [startDate, endDate]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Selected companies state (stores {code, sname} objects)
  const [selectedCompanyObjects, setSelectedCompanyObjects] = useState([]);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // News types mapping: display name -> API value
  const newsTypesMap = {
    "Government and Regulatory News": "government_and_regulatory_news",
    "New Product & Project Updates": "new_product_and_project_updates",
    "Marketing Initiatives": "marketing_initiatives",
    "Other Company News": "other_company_news",
    "Financial Announcements": "financial_announcements",
    "Mergers & Acquisitions": "mergers_and_acquisitions",
    "Contract": "contract",
    "Investment & Capital Raising": "investment_and_capital_raising",
    "Research & Development": "research_and_development",
    "Exhibitions & Conferences": "exhibitions_and_conferences",
    "Joint Ventures Collaborations Partnerships": "joint_ventures_collaborations_partnerships"
  };

  // Regions mapping: display name -> API value
  const regionsMap = {
    "Asia-Pacific": "AP",
    "North America": "NM",
    "Europe": "EU",
    "Middle East & Africa": "MA",
    "South America": "SA"
  };

  const newsTypes = ["All", ...Object.keys(newsTypesMap)];
  const regions = ["All", ...Object.keys(regionsMap)];

  // Debounced search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setIsDropdownOpen(false);
      return;
    }

    setIsLoading(true);
    setIsDropdownOpen(true);
    try {
      const results = await getSearchKeywords(query);
      // Filter out already selected companies
      const selectedCodes = selectedCompanyObjects.map(c => c.code);
      const filteredResults = results.filter(r => !selectedCodes.includes(r.value));
      setSearchResults(filteredResults);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setIsDropdownOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyObjects]);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  // Handle company selection
  const handleSelectCompany = (company) => {
    const newCompany = {
      code: company.value,
      sname: company.label
    };

    if (!selectedCompanyObjects.find(c => c.code === newCompany.code)) {
      const updatedSelected = [...selectedCompanyObjects, newCompany];
      setSelectedCompanyObjects(updatedSelected);
      setSelectedCompanies(updatedSelected.map(c => c.code));
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    setSearchQuery('');
    setSearchResults([]);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    searchInputRef.current?.blur();
  };

  // Handle company removal
  const handleRemoveCompany = (companyCode) => {
    const updatedSelected = selectedCompanyObjects.filter(c => c.code !== companyCode);
    setSelectedCompanyObjects(updatedSelected);
    setSelectedCompanies(updatedSelected.map(c => c.code));
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isDropdownOpen || searchResults.length === 0) {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
          handleSelectCompany(searchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Sync selectedCompanies (codes) to selectedCompanyObjects when codes change externally
  useLayoutEffect(() => {
    const currentCompanyObjectsMap = new Map(selectedCompanyObjects.map(c => [c.code, c]));

    let updatedSelectedCompanyObjects = [];

    selectedCompanies.forEach(code => {
      const existingObj = currentCompanyObjectsMap.get(code);
      if (existingObj) {
        const newName = companyNamesMap[code];
        if (newName && newName !== code && newName !== existingObj.sname) {
          updatedSelectedCompanyObjects.push({ ...existingObj, sname: newName });
        } else {
          updatedSelectedCompanyObjects.push(existingObj);
        }
      } else {
        const companyName = companyNamesMap[code] || code;
        updatedSelectedCompanyObjects.push({ code, sname: companyName });
      }
    });

    updatedSelectedCompanyObjects = updatedSelectedCompanyObjects.filter(obj =>
      selectedCompanies.includes(obj.code)
    );

    const currentCodes = selectedCompanyObjects.map(c => c.code).sort();
    const newCodes = updatedSelectedCompanyObjects.map(c => c.code).sort();

    if (JSON.stringify(currentCodes) !== JSON.stringify(newCodes) ||
      selectedCompanyObjects.some((obj, i) => obj.sname !== updatedSelectedCompanyObjects[i]?.sname)) {
      setSelectedCompanyObjects(updatedSelectedCompanyObjects);
    }
  }, [selectedCompanies, companyNamesMap]);

  // Lift state: Notify parent of code-to-name mapping for display
  useEffect(() => {
    if (onCompanyNameMapReady) {
      const mapping = selectedCompanyObjects.reduce((acc, obj) => {
        acc[obj.code] = obj.sname;
        return acc;
      }, {});
      onCompanyNameMapReady(mapping);
    }
  }, [selectedCompanyObjects, onCompanyNameMapReady]);


  const toggleFilter = (value, filterArray, setFilterArray) => {
    if (value === "All") {
      setFilterArray([]);
    } else {
      setFilterArray(prev =>
        prev.includes(value)
          ? prev.filter(item => item !== value)
          : [...prev, value]
      );
    }
  };

  // ── Industry News section (user's own industry) ──
  const userIndustryData = userIndustry ? industriesMap[userIndustry] : null;
  // Expanded by default
  const [industryNewsExpanded, setIndustryNewsExpanded] = useState(true);

  const handleIndustryNewsSubDomainToggle = (domainValue) => {
    if (selectedIndustryNewsDomains.includes(domainValue)) {
      setSelectedIndustryNewsDomains(prev => prev.filter(d => d !== domainValue));
    } else {
      setSelectedIndustryNewsDomains(prev => [...prev, domainValue]);
    }
  };

  // ── Customer Industries section (from consumerIndustry API) ──
  const [expandedCustomerIndustries, setExpandedCustomerIndustries] = useState({});

  // Filter industriesMap to only show industries returned by consumerIndustry API
  const filteredCustomerIndustries = useMemo(() => {
    if (!consumerIndustries || consumerIndustries.length === 0) return {};
    const filtered = {};
    consumerIndustries.forEach(name => {
      // Exclude the user's focus industry if it's already in the Industry News section
      if (industriesMap[name] && name !== userIndustry) {
        filtered[name] = industriesMap[name];
      }
    });
    return filtered;
  }, [consumerIndustries, userIndustry]);

  // Hide Customer Industries if no other industries are available (the primary one is in Industry News)
  const shouldShowCustomerIndustries = useMemo(() => {
    return Object.keys(filteredCustomerIndustries).length > 0;
  }, [filteredCustomerIndustries]);

  // "All" toggle for Customer Industries
  const isAllCustomerIndustriesSelected = useMemo(() => {
    if (!filteredCustomerIndustries || Object.keys(filteredCustomerIndustries).length === 0) return false;
    const allSubs = [];
    Object.values(filteredCustomerIndustries).forEach(data => {
      allSubs.push(...Object.values(data.domains));
    });
    return allSubs.length > 0 && allSubs.every(v => selectedCustomerDomains.includes(v));
  }, [filteredCustomerIndustries, selectedCustomerDomains]);

  const handleAllCustomerIndustriesToggle = () => {
    if (isAllCustomerIndustriesSelected) {
      setSelectedCustomerDomains([]);
    } else {
      const allSubs = [];
      Object.values(filteredCustomerIndustries).forEach(data => {
        allSubs.push(...Object.values(data.domains));
      });
      setSelectedCustomerDomains(allSubs);
    }
  };

  const handleCustomerIndustryToggle = (industryData) => {
    const allDomainValues = Object.values(industryData.domains);
    const isCurrentlyChecked = allDomainValues.every(v => selectedCustomerDomains.includes(v));

    if (isCurrentlyChecked) {
      setSelectedCustomerDomains(prev => prev.filter(d => !allDomainValues.includes(d)));
    } else {
      setSelectedCustomerDomains(prev => [...prev, ...allDomainValues.filter(v => !prev.includes(v))]);
    }
  };

  const handleCustomerSubDomainToggle = (domainValue) => {
    if (selectedCustomerDomains.includes(domainValue)) {
      setSelectedCustomerDomains(prev => prev.filter(d => d !== domainValue));
    } else {
      setSelectedCustomerDomains(prev => [...prev, domainValue]);
    }
  };

  const toggleExpandCustomerIndustry = (industryName) => {
    setExpandedCustomerIndustries(prev => ({
      ...prev,
      [industryName]: !prev[industryName]
    }));
  };

  return (
    <div className="filter-sidebar-news">
      <div className="filter-box-news">
        <h3 className="filter-title-news">Published Date</h3>
        <NewsLetterDateRangePicker
          startDate={localEndDate}
          endDate={localStartDate}
          onDateChange={(start, end) => {
            setLocalEndDate(start);
            setLocalStartDate(end);
            setEndDate(start);
            setStartDate(end);
          }}
        />
      </div>

      <div className="filter-box-news">
        <h3 className="filter-title-news">Companies</h3>

        {/* Company Search Input */}
        <div className="company-search-container-news">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchQuery.trim().length >= 2 && searchResults.length > 0) {
                setIsDropdownOpen(true);
              }
            }}
            placeholder="Type to search companies..."
            className="company-search-input-news"
          />

          {/* Dropdown Results */}
          {isDropdownOpen && (
            <div ref={dropdownRef} className="company-dropdown-news">
              {isLoading ? (
                <div className="company-dropdown-item-news loading-news">
                  Searching...
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <div
                    key={result.value}
                    className={`company-dropdown-item-news ${index === highlightedIndex ? 'highlighted-news' : ''
                      }`}
                    onClick={() => handleSelectCompany(result)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {result.label}
                  </div>
                ))
              ) : (
                <div className="company-dropdown-item-news no-results-news">
                  No companies found
                </div>
              )}
            </div>
          )}
        </div>

        {/* Checkbox list: "All" + selected companies */}
        <div className="checkbox-list-news">
          {/* All checkbox */}
          <label className="checkbox-label-news">
            <input
              type="checkbox"
              checked={selectedCompanies.length === 0 && selectedCompanyObjects.length === 0}
              onChange={() => {
                setSelectedCompanies([]);
                setSelectedCompanyObjects([]);
              }}
              className="checkbox-input-news"
            />
            <span>All</span>
          </label>

          {/* Selected companies from search - appear as checkboxes */}
          {selectedCompanyObjects.map(company => (
            <label key={company.code} className="checkbox-label-news">
              <input
                type="checkbox"
                checked={true}
                onChange={() => handleRemoveCompany(company.code)}
                className="checkbox-input-news"
              />
              <span>{company.sname}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ── Industry News ── */}
      {userIndustryData && (
        <div className="filter-box-news">
          <h3 className="filter-title-news">Industry News</h3>
          <div className="checkbox-list-news industry-list-news">
            <div className="industry-group-news">
              <div className="industry-item-news">
                <label className="checkbox-label-news">
                  <input
                    type="checkbox"
                    checked={
                      Object.values(userIndustryData.domains).length > 0 &&
                      Object.values(userIndustryData.domains).every(v => selectedIndustryNewsDomains.includes(v))
                    }
                    onChange={() => {
                      const allDomains = Object.values(userIndustryData.domains);
                      const isAllChecked = allDomains.every(v => selectedIndustryNewsDomains.includes(v));
                      if (isAllChecked) {
                        setSelectedIndustryNewsDomains(prev => prev.filter(d => !allDomains.includes(d)));
                      } else {
                        setSelectedIndustryNewsDomains(prev => [...prev, ...allDomains.filter(v => !prev.includes(v))]);
                      }
                    }}
                    className="checkbox-input-news"
                  />
                  <span>{userIndustry}</span>
                </label>
                <button
                  className={`industry-expand-arrow-news ${industryNewsExpanded ? 'expanded' : ''}`}
                  onClick={() => setIndustryNewsExpanded(prev => !prev)}
                  title="Show subindustries"
                >
                  ›
                </button>
              </div>

              {industryNewsExpanded && (
                <div className="subindustry-list-news">
                  {Object.entries(userIndustryData.domains).map(([subName, subValue]) => (
                    <label key={subValue} className="checkbox-label-news subindustry-item-news">
                      <input
                        type="checkbox"
                        checked={selectedIndustryNewsDomains.includes(subValue)}
                        onChange={() => handleIndustryNewsSubDomainToggle(subValue)}
                        className="checkbox-input-news"
                      />
                      <span>{subName}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Industries ── */}
      {shouldShowCustomerIndustries && (
        <div className="filter-box-news">
          <h3 className="filter-title-news">Customer Industries</h3>
          <div className="checkbox-list-news industry-list-news">
            {/* All checkbox */}
            <label className="checkbox-label-news">
              <input
                type="checkbox"
                checked={isAllCustomerIndustriesSelected}
                onChange={handleAllCustomerIndustriesToggle}
                className="checkbox-input-news"
              />
              <span>All</span>
            </label>

            {Object.entries(filteredCustomerIndustries).map(([industryName, industryData]) => {
              const industrySubs = Object.values(industryData.domains);
              const isChecked = industrySubs.length > 0 && industrySubs.every(v => selectedCustomerDomains.includes(v));
              const isExpanded = expandedCustomerIndustries[industryName];
              const domainEntries = Object.entries(industryData.domains);

              return (
                <div key={industryData.code} className="industry-group-news">
                  <div className="industry-item-news">
                    <label className="checkbox-label-news">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCustomerIndustryToggle(industryData)}
                        className="checkbox-input-news"
                      />
                      <span>{industryName}</span>
                    </label>
                    <button
                      className={`industry-expand-arrow-news ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleExpandCustomerIndustry(industryName)}
                      title="Show subindustries"
                    >
                      ›
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="subindustry-list-news">
                      {domainEntries.map(([subName, subValue]) => (
                        <label key={subValue} className="checkbox-label-news subindustry-item-news">
                          <input
                            type="checkbox"
                            checked={selectedCustomerDomains.includes(subValue)}
                            onChange={() => handleCustomerSubDomainToggle(subValue)}
                            className="checkbox-input-news"
                          />
                          <span>{subName}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="filter-box-news">
        <h3 className="filter-title-news">News Types</h3>
        <div className="checkbox-list-news">
          {newsTypes.map(type => {
            const apiValue = newsTypesMap[type];
            return (
              <label key={type} className="checkbox-label-news">
                <input
                  type="checkbox"
                  checked={type === "All" ? selectedNewsTypes.length === 0 : selectedNewsTypes.includes(apiValue)}
                  onChange={() => toggleFilter(type === "All" ? "All" : apiValue, selectedNewsTypes, setSelectedNewsTypes)}
                  className="checkbox-input-news"
                />
                <span>{type}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="filter-box-news">
        <h3 className="filter-title-news">Regions</h3>
        <div className="checkbox-list-news">
          {regions.map(region => {
            const apiValue = regionsMap[region];
            return (
              <label key={region} className="checkbox-label-news">
                <input
                  type="checkbox"
                  checked={region === "All" ? selectedRegions.length === 0 : selectedRegions.includes(apiValue)}
                  onChange={() => toggleFilter(region === "All" ? "All" : apiValue, selectedRegions, setSelectedRegions)}
                  className="checkbox-input-news"
                />
                <span>{region}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* ── Highlights ── */}
      {/* <div className="filter-box-news">
        <h3 className="filter-title-news">Highlights</h3>
        <div className="checkbox-list-news">
          <label className="checkbox-label-news">
            <input
              type="checkbox"
              checked={onlyWithTimeline}
              onChange={() => setOnlyWithTimeline(!onlyWithTimeline)}
              className="checkbox-input-news"
            />
            <span>News with Timelines</span>
          </label>
        </div>
      </div> */}
    </div>
  );
};

export default FilterSidebar;